import _ from 'lodash';
import uniqolor from 'uniqolor';
import {
  getOneOfVariants,
  pickOneOfVariant,
  resolveSchemaForForm,
} from './clientConfigSchemaResolve';

/**
 * Merge a partial form submit into the persisted config (e.g. lazy-mounted accordion fields).
 * Arrays from the submission replace the previous value entirely (lodash default merge merges by index).
 */
const omitSpuriousNullSubmissionValues = (persisted: unknown, submission: unknown): unknown => {

  if (submission === null) {

    if (persisted === undefined || persisted === null) {

      return undefined;

    }
    return null;

  }
  if (Array.isArray(submission)) {

    return submission;

  }
  if (!_.isPlainObject(submission)) {

    return submission;

  }

  const persistedObject: Record<string, unknown> = _.isPlainObject(persisted)
    ? persisted as Record<string, unknown>
    : {};
  const result: Record<string, unknown> = {};
  for (const [key, submittedValue] of Object.entries(submission as Record<string, unknown>)) {

    const sanitized = omitSpuriousNullSubmissionValues(persistedObject[key], submittedValue);
    if (sanitized !== undefined) {

      result[key] = sanitized;

    }

  }
  return result;

};

export const mergeConfigWithPartialFormSubmission = (
  persisted: Record<string, unknown> | undefined,
  submission: Record<string, unknown>,
) => {

  const sanitizedSubmission = omitSpuriousNullSubmissionValues(persisted, submission);
  const submissionRecord = _.isPlainObject(sanitizedSubmission) ? sanitizedSubmission : {};
  return _.mergeWith(
    _.cloneDeep(persisted ?? {}),
    submissionRecord as Record<string, unknown>,
    (_obj: unknown, srcVal: unknown) => (Array.isArray(srcVal) ? [...srcVal] : undefined),
  );

};

/** True when a value carries no config (null, empty containers, or nested shells with no leaves). */
export const isStructurallyEmptyClientConfigValue = (value: unknown): boolean => {

  if (value === null || value === undefined) {

    return true;

  }
  if (Array.isArray(value)) {

    return value.length === 0 || value.every(isStructurallyEmptyClientConfigValue);

  }
  if (_.isPlainObject(value)) {

    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    return keys.length === 0 || keys.every((key) => isStructurallyEmptyClientConfigValue(record[key]));

  }
  return false;

};

/** Top-level default unset: null, undefined, or empty structure from stripped partial forms. */
export const isUnsetClientConfigDefaultValue = (value: unknown): boolean => (
  isStructurallyEmptyClientConfigValue(value)
);

export const hasClientConfigDefaultValueChanged = (original: unknown, updated: unknown): boolean => {

  if (isUnsetClientConfigDefaultValue(original) && isUnsetClientConfigDefaultValue(updated)) {

    return false;

  }
  return !_.isEqual(original, updated);

};

const isUnsetOptionalValue = isStructurallyEmptyClientConfigValue;

const isOptionalClientConfigProperty = (
  parentSchema: Record<string, any>,
  key: string,
  propSchema: Record<string, any> | undefined,
): boolean => {

  const resolvedPropSchema = propSchema
    ? resolveSchemaForForm(propSchema) ?? propSchema
    : undefined;
  if (resolvedPropSchema?.['x-optional'] === true) {

    return true;

  }
  if (parentSchema.required?.includes(key)) {

    return false;

  }
  if (resolvedPropSchema?.nullable) {

    return true;

  }
  return true;

};

const isObjectLikeClientConfigSchema = (propSchema: Record<string, any> | undefined): boolean => {

  const resolved = propSchema ? resolveSchemaForForm(propSchema) ?? propSchema : undefined;
  return resolved?.type === 'object' || Boolean(resolved?.properties);

};

const isArrayLikeClientConfigSchema = (propSchema: Record<string, any> | undefined): boolean => {

  const resolved = propSchema ? resolveSchemaForForm(propSchema) ?? propSchema : undefined;
  return resolved?.type === 'array';

};

const emptyRequiredClientConfigValue = (propSchema: Record<string, any> | undefined): unknown => {

  if (isObjectLikeClientConfigSchema(propSchema)) {

    return {};

  }
  if (isArrayLikeClientConfigSchema(propSchema)) {

    return [];

  }
  return undefined;

};

const resolveActiveItemSchema = (itemSchema: Record<string, any> | undefined, item: unknown) => {

  const variants = getOneOfVariants(itemSchema);
  if (variants && _.isPlainObject(item)) {

    const record = item as Record<string, unknown>;
    for (const discriminator of ['mergeKey', 'type']) {

      const variant = pickOneOfVariant(variants, discriminator, record[discriminator]);
      if (variant) {

        return resolveSchemaForForm(variant) ?? variant;

      }

    }

  }
  return resolveSchemaForForm(itemSchema) ?? itemSchema;

};

/** Drop null/undefined (and empty optional objects) so partial overrides omit unset fields. */
export const stripUnsetOptionalClientConfigValue = (value: unknown, schema: Record<string, any> | undefined): unknown => {

  if (value === null || value === undefined) {

    return value;

  }

  if (Array.isArray(value)) {

    const itemSchema = schema?.items;
    if (!itemSchema) {

      return value;

    }
    const strippedItems = value
      .map((item) => stripUnsetOptionalClientConfigValue(item, resolveActiveItemSchema(itemSchema, item)))
      .filter((item) => !isStructurallyEmptyClientConfigValue(item));
    return strippedItems;

  }

  if (!_.isPlainObject(value)) {

    return value;

  }

  const resolvedSchema = resolveActiveItemSchema(schema, value);
  if (!resolvedSchema?.properties) {

    return value;

  }

  const result: Record<string, unknown> = {};
  for (const [key, propValue] of Object.entries(value as Record<string, unknown>)) {

    const propSchema = resolvedSchema.properties[key];
    if (!propSchema) {

      continue;

    }

    let stripped = stripUnsetOptionalClientConfigValue(propValue, propSchema);
    if (isUnsetOptionalValue(stripped)) {

      if (isOptionalClientConfigProperty(resolvedSchema, key, propSchema)) {

        continue;

      }
      const requiredEmptyValue = emptyRequiredClientConfigValue(propSchema);
      if (requiredEmptyValue === undefined) {

        if (propValue !== undefined) {

          result[key] = propValue;

        }
        continue;

      }
      stripped = stripUnsetOptionalClientConfigValue(requiredEmptyValue, propSchema);

    }
    result[key] = stripped;

  }

  for (const key of resolvedSchema.required ?? []) {

    if (result[key] !== undefined) {

      continue;

    }
    const propSchema = resolvedSchema.properties[key];
    if (!propSchema) {

      continue;

    }
    const requiredEmptyValue = emptyRequiredClientConfigValue(propSchema);
    if (requiredEmptyValue === undefined) {

      continue;

    }
    result[key] = stripUnsetOptionalClientConfigValue(requiredEmptyValue, propSchema);

  }

  return result;

};

export type ClientConfigGroupRef = {
  _id: string;
  _ref: 'ClientConfigGroup';
};

export type AppGroupDefinition = {
  type: 'group';
  group: ClientConfigGroupRef;
};

export type AppKeyDefinition = {
  type: 'key';
  key: string;
  value: any;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
};

export type AppDefinition = AppGroupDefinition | AppKeyDefinition;

export type AppConfigEntry = {
  definitions: AppDefinition[];
  filter?: {
    type: 'osFilter';
    osType: 'ios' | 'android';
    osVersionRange?: string;
  };
};

export type AppClientConfigContainer = {
  app?: AppConfigEntry[];
};

export type ClientConfigGroupLike = {
  _id: string;
  name: string;
  customer?: {
    _id: string;
    _ref?: string;
  } | null;
  clientConfig?: AppClientConfigContainer;
};

export function isClientConfigGroupGlobal(group?: Pick<ClientConfigGroupLike, 'customer'> | null): boolean {

  return !group?.customer?._id;

}

export function isClientConfigGroupCustomerSpecific(group?: Pick<ClientConfigGroupLike, 'customer'> | null): boolean {

  return Boolean(group?.customer?._id);

}

export function isClientConfigGroupVisibleForCustomer(
  group?: Pick<ClientConfigGroupLike, 'customer'> | null,
  currentCustomerId?: string | null,
): boolean {

  const groupCustomerId = group?.customer?._id;
  if (!groupCustomerId) {

    return true;

  }
  return Boolean(currentCustomerId && groupCustomerId === currentCustomerId);

}

export function getDisplayedSourceGroupIds(
  sourceGroupIds: string[],
  currentPathSources: string[],
  removedGroupIds: Set<string>,
  isLeaf: boolean,
): string[] {

  if (isLeaf) {

    return currentPathSources;

  }
  return sourceGroupIds.filter((groupId) => !removedGroupIds.has(groupId) || currentPathSources.includes(groupId));

}

export function isChangedKeyNewFromAddedGroup(
  currentPathSources: string[],
  previousPathSources: string[],
  addedGroupIds: Set<string>,
): boolean {

  if (previousPathSources.length > 0) {

    return false;

  }
  return currentPathSources.some((groupId) => addedGroupIds.has(groupId));

}

const uniqColorOptionsForClientConfigGroups = {
  saturation: [100, 100] as [number, number],
  lightness: [10, 80] as [number, number],
};

export function getClientConfigGroupColor(groupId: string): string {

  return uniqolor(groupId, uniqColorOptionsForClientConfigGroups).color;

}

export const CLIENT_CONFIG_INLINE_KEY_RAIL_COLOR = '#d9d9d9';

export function isConfigPathEqualOrDescendant(path: string, ancestorPath: string): boolean {

  return path === ancestorPath || path.startsWith(`${ancestorPath}.`);

}

export function hasActiveGroupSourcesAtOrUnderPath(
  sourcesByPath: Record<string, string[]>,
  path: string,
): boolean {

  return Object.keys(sourcesByPath).some(
    (sourcePath) => isConfigPathEqualOrDescendant(sourcePath, path)
      && (sourcesByPath[sourcePath]?.length ?? 0) > 0,
  );

}

function collectSourcePathsUnderPrefix(
  sourcesByPath: Record<string, string[]>,
  prefix: string,
): string[] {

  return Object.keys(sourcesByPath).filter((sourcePath) => isConfigPathEqualOrDescendant(sourcePath, prefix));

}

function moveGroupSourcesToOverriddenForPathAndDescendants(
  prefix: string,
  sourcesByPath: Record<string, string[]>,
  overriddenSourcesByPath: Record<string, string[]>,
): void {

  for (const path of collectSourcePathsUnderPrefix(sourcesByPath, prefix)) {

    const previousSources = sourcesByPath[path];
    if (!previousSources?.length) {

      continue;

    }
    overriddenSourcesByPath[path] = [...previousSources];
    delete sourcesByPath[path];

  }

}

export function getFirstAppConfigEntry(record: any): AppConfigEntry | null {

  const appConfig = record?.clientConfig?.app;
  if (!Array.isArray(appConfig) || appConfig.length === 0) {

    return null;

  }
  const first = appConfig[0];
  return {
    definitions: Array.isArray(first?.definitions) ? first.definitions : [],
    filter: first?.filter,
  };

}

export function splitDefinitions(definitions: AppDefinition[]) {

  const groupDefinitions: AppGroupDefinition[] = [];
  const keyDefinitions: AppKeyDefinition[] = [];
  for (const definition of definitions || []) {

    if (definition?.type === 'group') {

      groupDefinitions.push(definition);

    } else if (definition?.type === 'key') {

      keyDefinitions.push(definition);

    }

  }
  return { groupDefinitions, keyDefinitions };

}

const sortKeyDefinitions = (definitions: AppKeyDefinition[]) => (
  [...definitions].sort((left, right) => left.key.localeCompare(right.key))
);

export function areKeyDefinitionsEqual(
  left: AppKeyDefinition[],
  right: AppKeyDefinition[],
): boolean {

  return _.isEqual(sortKeyDefinitions(left), sortKeyDefinitions(right));

}

export function findKeyDefinitionByPath(
  definitions: AppKeyDefinition[],
  path: string,
): AppKeyDefinition | undefined {

  return definitions.find((definition) => definition.key === path);

}

export function removeKeyDefinitionByPath(
  definitions: AppKeyDefinition[],
  path: string,
): AppKeyDefinition[] {

  return definitions.filter((definition) => definition.key !== path);

}

function applyDefinitionsRecursive(
  config: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
  visitedGroups: Set<string>,
) {

  const { groupDefinitions, keyDefinitions } = splitDefinitions(definitions);

  for (const definition of groupDefinitions) {

    const groupId = definition.group?._id;
    if (!groupId || visitedGroups.has(groupId)) {

      continue;

    }
    const group = groupById[groupId];
    if (!group) {

      continue;

    }
    visitedGroups.add(groupId);
    const nestedDefinitions = getFirstAppConfigEntry(group)?.definitions || [];
    applyDefinitionsRecursive(config, nestedDefinitions, groupById, visitedGroups);
    visitedGroups.delete(groupId);

  }

  for (const definition of keyDefinitions) {

    _.set(config, definition.key, definition.value);

  }

}

function applyDefinitionsRecursiveWithSources(
  config: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
  visitedGroups: Set<string>,
  sourcesByPath: Record<string, string[]>,
  overriddenSourcesByPath: Record<string, string[]>,
  groupStack: string[],
) {

  const { groupDefinitions, keyDefinitions } = splitDefinitions(definitions);

  for (const definition of groupDefinitions) {

    const groupId = definition.group?._id;
    if (!groupId || visitedGroups.has(groupId)) {

      continue;

    }

    const group = groupById[groupId];
    if (!group) {

      continue;

    }

    visitedGroups.add(groupId);
    groupStack.push(groupId);
    const nestedDefinitions = getFirstAppConfigEntry(group)?.definitions || [];
    applyDefinitionsRecursiveWithSources(
      config,
      nestedDefinitions,
      groupById,
      visitedGroups,
      sourcesByPath,
      overriddenSourcesByPath,
      groupStack,
    );
    groupStack.pop();
    visitedGroups.delete(groupId);

  }

  for (const definition of keyDefinitions) {

    if (groupStack.length === 0) {

      moveGroupSourcesToOverriddenForPathAndDescendants(
        definition.key,
        sourcesByPath,
        overriddenSourcesByPath,
      );

    }

    _.set(config, definition.key, definition.value);
    if (groupStack.length > 0) {

      const existing = sourcesByPath[definition.key] || [];
      const next = [...existing];
      for (const groupId of groupStack) {

        if (!next.includes(groupId)) {

          next.push(groupId);

        }

      }
      sourcesByPath[definition.key] = next;

    }

  }

}

export function buildConfigFromDefinitions(
  defaultConfig: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
) {

  const config = _.cloneDeep(defaultConfig || {});
  applyDefinitionsRecursive(config, definitions, groupById, new Set<string>());
  return config;

}

export function buildConfigFromDefinitionsWithSources(
  defaultConfig: Record<string, any>,
  definitions: AppDefinition[],
  groupById: Record<string, ClientConfigGroupLike>,
) {

  const config = _.cloneDeep(defaultConfig || {});
  const sourcesByPath: Record<string, string[]> = {};
  const overriddenSourcesByPath: Record<string, string[]> = {};
  applyDefinitionsRecursiveWithSources(
    config,
    definitions,
    groupById,
    new Set<string>(),
    sourcesByPath,
    overriddenSourcesByPath,
    [],
  );
  return {
    config,
    sourcesByPath,
    overriddenSourcesByPath,
  };

}

export function getChangedObject(base: any, current: any): any {

  if (_.isEqual(base, current)) {

    return undefined;

  }
  if (!_.isPlainObject(current) || !_.isPlainObject(base)) {

    return current;

  }

  const changed: Record<string, any> = {};
  const keys = _.uniq([...Object.keys(base || {}), ...Object.keys(current || {})]);
  for (const key of keys) {

    const next = getChangedObject(base?.[key], current?.[key]);
    if (next !== undefined) {

      changed[key] = next;

    }

  }
  return changed;

}

export function flattenChangedLeafPaths(changed: any, prefix = ''): Array<{ path: string; value: any }> {

  if (!_.isPlainObject(changed) || Array.isArray(changed)) {

    return [{ path: prefix, value: changed }];

  }

  const entries: Array<{ path: string; value: any }> = [];
  for (const key of Object.keys(changed)) {

    const nextPath = prefix ? `${prefix}.${key}` : key;
    const value = changed[key];
    if (_.isPlainObject(value) && !Array.isArray(value)) {

      entries.push(...flattenChangedLeafPaths(value, nextPath));

    } else {

      entries.push({ path: nextPath, value });

    }

  }

  return entries;

}

export type ChangedPreviewNode = {
  key: string;
  path: string;
  value: any;
  isObjectNode: boolean;
  children: ChangedPreviewNode[];
  sourceGroupIds: string[];
};

function aggregateSourceIdsFromChildren(children: ChangedPreviewNode[]): string[] {

  const result: string[] = [];
  for (const child of children) {

    for (const sourceGroupId of child.sourceGroupIds) {

      if (!result.includes(sourceGroupId)) {

        result.push(sourceGroupId);

      }

    }

  }
  return result;

}

export function buildChangedPreviewTree(
  changed: any,
  sourcesByPath: Record<string, string[]>,
  prefix = '',
): ChangedPreviewNode[] {

  if (!_.isPlainObject(changed) || Array.isArray(changed)) {

    return [];

  }

  const nodes: ChangedPreviewNode[] = [];
  for (const key of Object.keys(changed)) {

    const path = prefix ? `${prefix}.${key}` : key;
    const value = changed[key];
    const isObjectNode = _.isPlainObject(value) && !Array.isArray(value);
    const children = isObjectNode ? buildChangedPreviewTree(value, sourcesByPath, path) : [];
    const sourceGroupIds = isObjectNode
      ? aggregateSourceIdsFromChildren(children)
      : (sourcesByPath[path] || []);

    nodes.push({
      key,
      path,
      value,
      isObjectNode,
      children,
      sourceGroupIds,
    });

  }

  const collapseSingleChildObjects = (node: ChangedPreviewNode): ChangedPreviewNode => {

    if (!node.isObjectNode) {

      return node;

    }

    const collapsedChildren = node.children.map((child) => collapseSingleChildObjects(child));
    let collapsedNode: ChangedPreviewNode = {
      ...node,
      children: collapsedChildren,
      sourceGroupIds: aggregateSourceIdsFromChildren(collapsedChildren),
    };

    while (collapsedNode.isObjectNode && collapsedNode.children.length === 1) {

      const onlyChild = collapsedNode.children[0];
      collapsedNode = {
        ...onlyChild,
        key: `${collapsedNode.key}.${onlyChild.key}`,
      };

    }

    return collapsedNode;

  };

  return nodes.map((node) => collapseSingleChildObjects(node));

}
